/**
 * LegalPage — Public page for /privacy-policy and /terms-and-conditions.
 * Fetches content from backend API — no hardcoded text.
 *
 * iter145 — Content is now HTML (sourced from Admin Panel). Rendered via
 * DOMPurify-sanitised `dangerouslySetInnerHTML`.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ChevronLeft, Shield, FileText, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const sanitise = (html) => DOMPurify.sanitize(html || '', {
  USE_PROFILES: { html: true },
  ADD_ATTR: ['target', 'rel'],
});

const LegalPage = ({ type }) => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState('');

  const isPrivacy = type === 'privacy';
  const title = isPrivacy ? 'Privacy Policy' : 'Terms & Conditions';
  const Icon = isPrivacy ? Shield : FileText;
  const iconColor = isPrivacy ? 'text-[#1B2A7B]' : 'text-blue-600';
  const iconBg = isPrivacy ? 'bg-[#EEF1FC]' : 'bg-blue-100';

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/legal/policy`);
        const data = await res.json();
        if (data.STATUS === 1) {
          setContent(isPrivacy ? data.RESULT.privacy_policy : data.RESULT.terms_and_conditions);
          setVersion(data.RESULT.version);
        }
      } catch (e) {
        setContent('Unable to load content. Please try again later.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isPrivacy]);

  return (
    <div className="min-h-screen bg-white" data-testid={`legal-page-${type}`}>
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center gap-3 px-4 py-3 max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="legal-back-btn"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className={`w-8 h-8 ${iconBg} rounded-lg flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${iconColor}`} />
          </div>
          <div>
            <h1 className="font-semibold text-gray-900 text-sm">{title}</h1>
            {version && <p className="text-xs text-gray-400">Version {version}</p>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-5 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : (
          <div className="max-w-none text-gray-700 leading-relaxed" data-testid="legal-content">
            {content ? (
              <div
                className="policy-html-content text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitise(content) }}
              />
            ) : (
              <p className="text-sm text-gray-500">Content not available.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalPage;
